import { useEffect, useState } from 'react';

/**
 * The peaks file for a track, fetched once.
 *
 * `TranscodeTrack` writes 1800 points per track (§4.3), each the true amplitude
 * of that slice relative to full scale - deliberately not normalised, so the
 * information that one track is quieter than another survives. Every consumer
 * normalises for display in its own way.
 *
 * Shared because there are two of them now: the scrubber's waveform and the
 * full-screen visualiser. Both want the same array from the same URL, and two
 * copies of a fetch-and-parse is two places for a missing file to be handled
 * differently.
 *
 * Null covers everything that can go wrong - no waveform, a 404, a truncated
 * body - because none of them is worth telling anybody about. A waveform is
 * cosmetic; the track still plays, and a broken-image box where a wave should
 * be is worse than nothing at all.
 */
export function usePeaks(url: string | null): number[] | null {
  const [peaks, setPeaks] = useState<number[] | null>(null);

  useEffect(() => {
    if (!url) {
      setPeaks(null);

      return;
    }

    let cancelled = false;

    fetch(url)
      .then((response) => response.json())
      .then((data: { peaks?: number[] }) => {
        if (!cancelled) setPeaks(data.peaks ?? null);
      })
      .catch(() => {
        if (!cancelled) setPeaks(null);
      });

    return () => {
      cancelled = true;
    };
  }, [url]);

  return peaks;
}

/**
 * The scale that makes the tallest peak reach the top.
 *
 * Rendering stored peaks raw means a quiet or dynamic recording draws as a flat
 * line while a squashed master fills the box, and the waveform stops conveying
 * shape at all. Normalising for display is what every player does, and it is a
 * rendering decision rather than a change to the data - which is why it lives
 * here rather than in the transcoder.
 */
export function displayScale(peaks: number[] | null): number {
  if (peaks === null || peaks.length === 0) return 1;

  const loudest = Math.max(...peaks);

  return loudest > 0 ? 1 / loudest : 1;
}
